import {useEffect} from 'react'

import {COLUMN_PAGING_POSITION, ROW_PAGING_POSITION} from '../focus/positions'
import type {BoardFocusPosition} from '../focus/types'
import {useBoardConfig, useBoardDataContext, useBoardPaging} from '../provider/contexts'
import type {ColumnKey, RowKey} from '../types'
import type {PagingLead} from './paging-lead'
import type {PagingSentinelProps} from './use-paging-sentinel'
import {usePagingStop} from './use-paging-stop'

/**
 * Starting values to be measured, not findings: they trade pages the reader turns back from
 * against loaders seen. The floor is the flat distance every direction used before this.
 */
const AXIS_LEAD = {fraction: 0.5, minPixels: 200, maxPixels: 600} as const

const COLUMN_LEAD: PagingLead = {axis: 'inline', ...AXIS_LEAD}
const ROW_LEAD: PagingLead = {axis: 'block', ...AXIS_LEAD}

export type UseBoardAxisPagingResult = {
  /**
   * This axis has a further page. Mount the ghost when it is true and nothing when it is
   * false.
   *
   * False whenever the axis is truncated, whatever the caller stated, because a ghost
   * that pages a server which has stopped enumerating is a dead end.
   */
  hasMore: boolean
  /** A page for this axis is in flight. The other axis is unaffected either way. */
  isLoading: boolean
  /**
   * The server stopped enumerating this axis. Never true at the same time as `hasMore`,
   * because they are opposite advice: one is a reason to keep scrolling and the other is
   * a reason to narrow the search.
   */
  isTruncated: boolean
  /** The caller's name for this axis's paging control. */
  label: string
  /** The caller's end-of-board notice for this axis. */
  truncationLabel: string
  sentinelProps: PagingSentinelProps
}

type Axis = {
  hasMore: boolean
  isLoading: boolean
  isTruncated: boolean
  label: string
  truncationLabel: string
  onLoadMore: ((visibleKeys?: readonly string[]) => void) | undefined
  /** The keys the renderer draws on the other axis, handed to `onLoadMore` as they stand when it fires. */
  visibleKeys: readonly string[] | undefined
  /** Where this axis's ghost sits on the board. One per board, so one constant per axis. */
  position: BoardFocusPosition
  lead: PagingLead
  name: 'columns' | 'rows'
}

/**
 * The half of an axis's paging that both axes do identically.
 *
 * Sharing it is what keeps the two from drifting. It does not make the axes one
 * direction: each one has its own callback, its own control and its own strings, and
 * reaching one never asks about the other. The duplicate-fetch guard is not here either,
 * for the same reason it is not repeated in `useBoardItemPaging`: it lives in
 * `usePagingSentinel`, which is the one mechanism all three directions already share.
 */
function useAxisPaging(axis: Axis): UseBoardAxisPagingResult {
  const {onVisibleKeysChange} = useBoardPaging()
  const {name, visibleKeys} = axis
  // Keyed on content: the renderer rebuilds this array per render, so its identity would report
  // on every one. Serialised rather than joined, so a single blank key stays one key.
  const visibleKeysId = visibleKeys === undefined ? undefined : JSON.stringify(visibleKeys)

  useEffect(() => {
    if (visibleKeysId === undefined) return

    onVisibleKeysChange?.(name, JSON.parse(visibleKeysId) as readonly string[])
  }, [name, onVisibleKeysChange, visibleKeysId])

  const hasMore = axis.hasMore && !axis.isTruncated
  const sentinelProps = usePagingStop({
    position: axis.position,
    label: axis.label,
    onReached: () => axis.onLoadMore?.(axis.visibleKeys),
    enabled: hasMore && axis.onLoadMore !== undefined,
    // This axis's own loading state. The other axis paging leaves this sentinel free to
    // ask, because the page in flight is not the one it would be asking for.
    isLoading: axis.isLoading,
    lead: axis.lead,
    // An axis grows past the end of what is drawn, so a page arriving moves nothing the reader
    // is scrolling from. Not true of a cell, which is why only the axes take this.
    asksWhileMoving: true,
  })

  return {
    hasMore,
    isLoading: axis.isLoading,
    isTruncated: axis.isTruncated,
    label: axis.label,
    truncationLabel: axis.truncationLabel,
    sentinelProps,
  }
}

export type UseBoardColumnPagingOptions = {
  /**
   * The rows the renderer draws right now, in board order. Passed to `onLoadMoreColumns`
   * when the end of the columns is reached. Leave it out and the callback gets `undefined`.
   */
  visibleRowKeys?: readonly RowKey[]
}

/**
 * More columns: the second of the board's three paging directions.
 *
 * Independent of the other two. Reaching the end of the columns asks for more columns
 * and says nothing about the rows or about any cell, and a board fetching more columns
 * puts nothing else into a loading state.
 */
export function useBoardColumnPaging({visibleRowKeys}: UseBoardColumnPagingOptions = {}): UseBoardAxisPagingResult {
  const {data} = useBoardDataContext()
  const {labels} = useBoardConfig()
  const {onLoadMoreColumns} = useBoardPaging()

  return useAxisPaging({
    hasMore: data.hasMoreColumns,
    isLoading: data.isLoadingColumns ?? false,
    isTruncated: data.columnsTruncated ?? false,
    label: labels.loadMoreColumns,
    truncationLabel: labels.columnsTruncated,
    onLoadMore: onLoadMoreColumns,
    visibleKeys: visibleRowKeys,
    position: COLUMN_PAGING_POSITION,
    name: 'columns',
    lead: COLUMN_LEAD,
  })
}

export type UseBoardRowPagingOptions = {
  /**
   * The columns the renderer draws right now, in board order. Passed to `onLoadMoreRows`
   * when the end of the rows is reached. Leave it out and the callback gets `undefined`.
   */
  visibleColumnKeys?: readonly ColumnKey[]
}

/**
 * More rows: the third of the board's three paging directions.
 *
 * A board with no swimlane has exactly one row and runs this same hook. It is not a
 * special path: such a board simply states `hasMoreRows: false`, which is the ordinary
 * way for any axis to say it is complete.
 */
export function useBoardRowPaging({visibleColumnKeys}: UseBoardRowPagingOptions = {}): UseBoardAxisPagingResult {
  const {data} = useBoardDataContext()
  const {labels} = useBoardConfig()
  const {onLoadMoreRows} = useBoardPaging()

  return useAxisPaging({
    hasMore: data.hasMoreRows,
    isLoading: data.isLoadingRows ?? false,
    isTruncated: data.rowsTruncated ?? false,
    label: labels.loadMoreRows,
    truncationLabel: labels.rowsTruncated,
    onLoadMore: onLoadMoreRows,
    visibleKeys: visibleColumnKeys,
    position: ROW_PAGING_POSITION,
    name: 'rows',
    lead: ROW_LEAD,
  })
}
